import { boundingExtent } from 'ol/extent.js';
import { Map, View, Feature } from 'ol';
import { Point } from 'ol/geom';
import { Control } from 'ol/control';
import { SVG_MODELS, SVG_WRAPPERS } from './SvgLibrary.js';
import { Text, Fill, Stroke, Style, Icon } from 'ol/style';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';

const GRID_SIZE = 25;
export const ROTATE_STEP = 15;

/**
 * Zooms the map to the extent of the floorplan represented by the features in the vector source.
 * @param {Map} map - The OpenLayers map instance.
 * @param {VectorSource} vectorSource - The vector source containing the floorplan features.
 * @param {number} [padding=50] - Padding around the extent in pixels.
 * @param {number} [zoomOffset=0] - Extra zoom levels applied after fitting (used to start a bit more zoomed in).
 * @returns {void}
 */
export function zoomToFloorplanExtent(map, vectorSource, padding = 50, zoomOffset = 0) {
    if (!map || !vectorSource) {
        console.warn('Map or vector source is not defined');
        return;
    }
    const features = vectorSource.getFeatures();
    if (features.length === 0) return;

    const extent = boundingExtent(features.map((f) => f.getGeometry().getExtent()));

    extent[0] -= padding;
    extent[1] -= padding;
    extent[2] += padding;
    extent[3] += padding;

    const view = map.getView();
    if (zoomOffset) {
        view.fit(extent, { duration: 0 });
        view.animate({ zoom: view.getZoom() + zoomOffset, duration: 0 });
    } else {
        view.fit(extent, { duration: 300 });
    }
}

/**
 * Changes the zoom level of the map view by a specified delta.
 * @function
 * @param {Map} mapView - The OpenLayers map view instance.
 * @param {number} delta - The amount to change the zoom level by (positive or negative).
 * @returns {void}
 */
export function zoomChange(mapView, delta) {
    mapView.animate({ zoom: mapView.getZoom() + delta, duration: 300 });
}

/**
 * Calculates the reverse scale based on the zoom level and original scale.
 * @param {number} zoom - The current zoom level of the map.
 * @param {number} originalScale - The original scale of the item.
 * @returns {number} - The calculated scale for the item at the current zoom level.
 */
export function reverseScale(zoom, originalScale) {
    return (1 / Math.pow(2, 20 - zoom)) * 10 * originalScale;
}

/**
 * Creates a style for an item based on its properties and the current zoom level.
 * @param {Object} item - The item object containing properties like model, svg, scale, rotation, and name.
 * @param {number} zoom - The current zoom level of the map.
 * @param {string} [lang='en'] - The language for the item name, default is 'en'.
 * @param {Object} [extraProps={}] - Additional properties to apply to the style, such as color or opacity.
 * @returns {Style} - The OpenLayers style object for the item.
 */
export function makeStyleForItem(item, zoom, lang = 'en', extraProps = {}, removeLabels = false) {
    let svgItem =
        item.svg || SVG_MODELS[item.model] || SVG_MODELS['chair_1'] || SVG_MODELS['vip_chair_1'];
    const svgWithWrapper =
        SVG_WRAPPERS[item.model] || SVG_WRAPPERS['chair_1'] || SVG_WRAPPERS['vip_chair_1'];
    svgItem = svgWithWrapper.replace('{{SVG_CONTENT}}', svgItem);

    const nameInLang = item?.nameI18n && item?.nameI18n[lang] ? item?.nameI18n[lang] : item.name;
    // display label for the "stage" item only
    const isStage = item.model === 'stage';
    const label = removeLabels && !isStage ? '' : nameInLang;

    // Rotate text label for vertical table models so it reads bottom-to-top
    const isVerticalTable = item.model && item.model.startsWith('table_v_');
    const textRotation = isVerticalTable ? -Math.PI / 2 : 0;

    // Scale font with zoom so labels grow/shrink with their elements
    const iconScale = reverseScale(zoom, item.scale || 0.5);
    const fontSize = Math.max(8, Math.round(iconScale * 3));

    const style = new Style({
        image: new Icon({
            src: `data:image/svg+xml;base64,${btoa(svgItem)}`,
            scale: iconScale,
            // rotation stored in degrees, convert to radians
            rotation: (item.rotation * Math.PI) / 180,
            anchor: [0.5, 0.5],
            ...extraProps,
        }),
        text: new Text({
            text: label,
            font: `${fontSize}px sans-serif`,
            fill: new Fill({ color: 'black' }),
            stroke: new Stroke({ color: 'white', width: 2 }),
            textAlign: 'center',
            textBaseline: 'middle',
            offsetY: 0,
            rotation: textRotation,
        }),
    });
    return style;
}

/**
 * Creates a feature from an item with its geometry and style.
 * @param {Object} item - The item object containing properties like id, x, y, and other attributes.
 * @param {string} lang - The language for the item name, used in the feature's style.
 * @param {number} zoom - The current zoom level of the map.
 * @param {Function|boolean} [onClick=false] - Optional click handler function for the feature, or false to disable click handling.
 * @param {Object} [extraProps={}] - Additional properties to apply to the feature's style, such as color or opacity.
 * @returns {Feature} - The OpenLayers feature object representing the item.
 */
export function featureFromItem(
    item,
    lang,
    zoom,
    onClick = false,
    extraProps = {},
    removeLabels = false,
) {
    const feature = new Feature({
        geometry: new Point([item.x, item.y]),
        itemId: item.id,
    });
    feature.setId(item.id);
    feature.setStyle(makeStyleForItem(item, zoom, lang, extraProps, removeLabels));
    feature.originalItem = item;
    if (onClick !== false) {
        feature.onClick = () => onClick(item.id);
    }
    return feature;
}

/**
 * Creates an OpenLayers map with specified controls and a vector layer.
 * @param {Object} mapRef - The reference to the HTML element where the map will be rendered.
 * @param {VectorLayer} vectorLayer - The vector layer to be added to the map.
 * @param {Array} [controls=[]] - An array of OpenLayers controls to be added to the map.
 * @param {Object} [viewOptions={}] - Options for the map view, such as zoom level, projection, etc.
 * @returns {Map} - The created OpenLayers map instance.
 */
export function createMapWithControls(mapRef, vectorLayer, controls = [], viewOptions = {}) {
    const defaultZoom = 17; // Default zoom level
    const map = new Map({
        controls: [],
        target: mapRef.current,
        layers: [vectorLayer],
        view: new View({
            zoom: defaultZoom,
            // set default minZoom and maxZoom if not provided
            minZoom: viewOptions.minZoom || defaultZoom - 2,
            maxZoom: viewOptions.maxZoom || defaultZoom + 1,
            projection: 'EPSG:3857',
            multiWorld: false,
            constrainOnlyCenter: true,
            constrainResolution: true,
            ...viewOptions,
        }),
    });
    // Add controls to the map
    controls.forEach((control) => {
        map.addControl(control);
    });
    // Set minZoom if supported
    if (map.getView().setMinZoom) {
        map.getView().setMinZoom(viewOptions.minZoom || defaultZoom - 2);
    }
    // Set maxZoom if supported
    if (map.getView().setMaxZoom) {
        map.getView().setMaxZoom(viewOptions.maxZoom || defaultZoom + 1);
    }
    return map;
}

/**
 * Creates a button control for the OpenLayers map.
 * @param {Object} options - Options for the button control.
 * @param {string} options.label - The label for the button.
 * @param {string} options.title - The title for the button (tooltip).
 * @param {Function} options.onClick - The click handler function for the button.
 * @param {string} [options.className='ol-zoom-in'] - The CSS class for the button.
 * @param {string} [options.divClassName='ol-unselectable ol-control'] - The CSS class for the control container.
 * @return {Control} - The OpenLayers control object for the button.
 */
export function createButtonControl({
    label,
    title,
    onClick,
    className = 'ol-zoom-in',
    divClassName = 'ol-unselectable ol-control',
}) {
    const button = document.createElement('button');
    button.type = 'button';
    button.innerHTML = label;
    button.title = title;
    button.className = className;
    button.addEventListener('click', onClick);

    const element = document.createElement('div');
    element.className = divClassName;
    element.style.margin = '5px';
    element.appendChild(button);

    return new Control({ element });
}

/**
 * Creates a vector layer and source for the OpenLayers map.
 * @returns {Object} - An object containing the vector source and vector layer.
 * @property {VectorSource} vectorSource - The OpenLayers vector source.
 * @property {VectorLayer} vectorLayer - The OpenLayers vector layer.
 */
export function createVectorLayerAndSource() {
    const vectorSource = new VectorSource();

    const vectorLayer = new VectorLayer({
        source: vectorSource,
        updateWhileAnimating: true,
        updateWhileInteracting: true,
        renderBuffer: 500,
    });

    return { vectorSource, vectorLayer };
}

/**
 * Snaps an item's position and rotation to the nearest grid size and rotation step.
 * @param {Object} item - The item object containing x, y, and rotation properties
 * @param {number} item.x - The x-coordinate of the item.
 * @param {number} item.y - The y-coordinate of the item.
 * @param {number} item.rotation - The rotation of the item in degrees.
 * @returns {Object} - A new item object with snapped x, y, and rotation properties.
 */
export function snapToGrid(item) {
    const snappedX = Math.round(item.x / GRID_SIZE) * GRID_SIZE;
    const snappedY = Math.round(item.y / GRID_SIZE) * GRID_SIZE;
    const snappedRotation = Math.round(item.rotation / ROTATE_STEP) * ROTATE_STEP;
    return {
        ...item,
        x: snappedX,
        y: snappedY,
        rotation: snappedRotation,
    };
}
